/* ================================================================
   AKK Product Shop — Interactive Logic
   ---------------------------------------------------------------
   Handles:
   - Thumbnail gallery switching
   - Variant selection (button-style option picker)
   - Quantity increment/decrement
   - Add to cart (AJAX) with loading state
   - URL update on variant change
   ================================================================ */

(function () {
  'use strict';

  const productSection = document.querySelector('.akk-product-shop');
  if (!productSection) return;

  // ---- Product data from global (injected by Liquid) ----
  // We'll parse variants from the form and media items
  const form = productSection.querySelector('.akk-product-shop__form');
  const variantIdInput = productSection.querySelector('.akk-product-shop__variant-id');
  const priceEl = productSection.querySelector('.akk-product-shop__price');
  const mediaItems = productSection.querySelectorAll('.akk-product-shop__media-item');
  const thumbs = productSection.querySelectorAll('.akk-product-shop__thumb');
  const errorEl = productSection.querySelector('.akk-product-shop__error-message');
  const atcBtn = productSection.querySelector('.akk-product-shop__atc');
  const inventoryEl = productSection.querySelector('.akk-product-shop__inventory-status');

  // ---- Variant data lookup ----
  // Build a map from option values to variant
  const productJson = form.querySelector('[data-product-json]');
  let product = null;

  // Try to get product data from meta tag or script
  const productScript = document.querySelector('script[type="application/json"][data-product]');
  if (productScript) {
    try {
      product = JSON.parse(productScript.textContent);
    } catch (e) {
      product = null;
    }
  }

  // If no product JSON available, fetch it
  if (!product) {
    const productHandle = window.location.pathname.split('/').pop();
    fetch(`/products/${productHandle}.js`)
      .then(r => r.json())
      .then(data => {
        product = data;
        initVariantOptions();
      })
      .catch(() => {
        // Fallback: basic button toggle without variant lookup
        initBasicVariantToggle();
      });
  } else {
    initVariantOptions();
  }

  // ---- Thumbnail gallery ----
  thumbs.forEach(thumb => {
    thumb.addEventListener('click', () => {
      const mediaId = thumb.dataset.mediaId;
      showMedia(mediaId);
    });
  });

  function showMedia(mediaId) {
    mediaItems.forEach(item => {
      item.classList.toggle('is-active', item.dataset.mediaId === mediaId);
    });
    thumbs.forEach(thumb => {
      thumb.classList.toggle('is-active', thumb.dataset.mediaId === mediaId);
    });
  }

  // ---- Quantity ----
  const qtyInput = productSection.querySelector('.akk-product-shop__qty-input');
  const qtyMinus = productSection.querySelector('[data-qty="minus"]');
  const qtyPlus = productSection.querySelector('[data-qty="plus"]');

  if (qtyMinus && qtyInput) {
    qtyMinus.addEventListener('click', () => {
      const val = parseInt(qtyInput.value, 10) || 1;
      if (val > 1) qtyInput.value = val - 1;
    });
  }

  if (qtyPlus && qtyInput) {
    qtyPlus.addEventListener('click', () => {
      const val = parseInt(qtyInput.value, 10) || 1;
      qtyInput.value = val + 1;
    });
  }

  if (qtyInput) {
    qtyInput.addEventListener('change', () => {
      const val = parseInt(qtyInput.value, 10);
      if (isNaN(val) || val < 1) qtyInput.value = 1;
    });
  }

  // ---- Variant selection ----
  let selectedOptions = [];

  function initVariantOptions() {
    if (!product || !product.options) return;
    const numOptions = product.options.length;

    // Initialize selected options from current variant
    const currentVariant = findCurrentVariant();
    if (currentVariant) {
      selectedOptions = [...currentVariant.options];
    } else {
      // Default to first values
      selectedOptions = product.options.map(o => o.values[0]);
    }

    // Set up click handlers
    const variantOptions = productSection.querySelectorAll('.akk-product-shop__variant-option');
    variantOptions.forEach(btn => {
      btn.addEventListener('click', () => {
        if (btn.classList.contains('is-disabled')) return;
        const optIndex = parseInt(btn.dataset.optionIndex, 10);
        const optValue = btn.dataset.optionValue;
        selectOption(optIndex, optValue);
      });
    });

    updateVariantUI();
  }

  function initBasicVariantToggle() {
    // Simple toggle without full variant lookup
    const variants = productSection.querySelectorAll('.akk-product-shop__variant');
    variants.forEach(variant => {
      const options = variant.querySelectorAll('.akk-product-shop__variant-option');
      options.forEach(btn => {
        btn.addEventListener('click', () => {
          if (btn.classList.contains('is-disabled')) return;
          options.forEach(o => o.classList.remove('is-active'));
          btn.classList.add('is-active');
          // Update label value
          const label = variant.querySelector('.akk-product-shop__variant-value');
          if (label) label.textContent = btn.dataset.optionValue;
        });
      });
    });
  }

  function selectOption(optionIndex, value) {
    selectedOptions[optionIndex] = value;

    // Update UI
    const optionBtns = productSection.querySelectorAll(
      `.akk-product-shop__variant-option[data-option-index="${optionIndex}"]`
    );
    optionBtns.forEach(btn => {
      btn.classList.toggle('is-active', btn.dataset.optionValue === value);
    });

    // Update label value
    const variantGroups = productSection.querySelectorAll('.akk-product-shop__variant');
    const group = variantGroups[optionIndex];
    if (group) {
      const label = group.querySelector('.akk-product-shop__variant-value');
      if (label) label.textContent = value;
    }

    updateVariantUI();
  }

  function updateVariantUI() {
    if (!product) return;

    // Find matching variant
    const variant = findVariantByOptions(selectedOptions);
    if (!variant) return;

    // Update hidden input
    variantIdInput.value = variant.id;

    // Update price
    updatePrice(variant);

    // Update image
    if (variant.featured_image && variant.featured_image.id) {
      const mediaId = variant.featured_image.id.toString();
      showMediaByVariantImage(mediaId);
    }

    // Update URL
    if (window.history && window.history.replaceState) {
      const url = new URL(window.location);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({ variant: variant.id }, '', url);
    }

    // Update button state
    if (atcBtn) {
      if (variant.available) {
        atcBtn.disabled = false;
        atcBtn.querySelector('.akk-product-shop__atc-text').textContent = 'Add to cart';
      } else {
        atcBtn.disabled = true;
        atcBtn.querySelector('.akk-product-shop__atc-text').textContent = 'Sold out';
      }
    }

    // Update inventory
    if (inventoryEl) {
      if (variant.inventory_management && variant.inventory_policy !== 'continue') {
        if (variant.inventory_quantity > 0) {
          inventoryEl.textContent = `Only ${variant.inventory_quantity} left in stock`;
        } else {
          inventoryEl.textContent = 'Out of stock';
        }
      } else {
        inventoryEl.textContent = 'In stock';
      }
    }
  }

  function findCurrentVariant() {
    if (!product) return null;
    // Try from URL param
    const urlParams = new URLSearchParams(window.location.search);
    const variantId = urlParams.get('variant');
    if (variantId) {
      const v = product.variants.find(v => v.id.toString() === variantId.toString());
      if (v) return v;
    }
    // Fallback to first available
    return product.variants.find(v => v.available) || product.variants[0];
  }

  function findVariantByOptions(options) {
    if (!product) return null;
    return product.variants.find(v => {
      return v.options.every((opt, i) => opt === options[i]);
    });
  }

  function updatePrice(variant) {
    if (!priceEl) return;
    const price = variant.price / 100;
    const compareAt = variant.compare_at_price / 100;
    const moneyFormat = window.Shopify?.moneyFormat || '${{amount}}';

    const formatMoney = (amount) => {
      // Simple money formatting
      return '$' + amount.toFixed(2);
    };

    if (compareAt > price) {
      const save = compareAt - price;
      priceEl.innerHTML = `
        <span class="akk-product-shop__price-sale">${formatMoney(price)}</span>
        <span class="akk-product-shop__price-compare">${formatMoney(compareAt)}</span>
        <span class="akk-product-shop__price-save">Save ${formatMoney(save)}</span>
      `;
    } else {
      priceEl.innerHTML = `<span class="akk-product-shop__price-regular">${formatMoney(price)}</span>`;
    }
  }

  function showMediaByVariantImage(imageId) {
    // Try to match by data-variant-ids
    let found = false;
    mediaItems.forEach(item => {
      const vIds = item.dataset.variantIds;
      if (vIds && vIds.split(',').includes(imageId)) {
        // Check if we can match
      }
    });

    // Simpler: find media with matching image by checking src
    const thumb = Array.from(thumbs).find(t => {
      const img = t.querySelector('img');
      return img && img.src.includes(imageId);
    });
    if (thumb) {
      showMedia(thumb.dataset.mediaId);
      return;
    }

    // Fallback: show first
    if (mediaItems.length > 0) {
      showMedia(mediaItems[0].dataset.mediaId);
    }
  }

  // ---- Add to cart (AJAX) ----
  if (form && atcBtn) {
    form.addEventListener('submit', async (e) => {
      e.preventDefault();

      const id = variantIdInput.value;
      const quantity = parseInt(qtyInput?.value || '1', 10);

      if (!id) return;

      atcBtn.classList.add('is-loading');
      atcBtn.disabled = true;
      errorEl?.classList.remove('is-visible');

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            id: parseInt(id, 10),
            quantity: quantity
          })
        });

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.description || data.message || 'Failed to add to cart');
        }

        // Success: update cart count and show cart drawer or navigate
        await updateCartCount();

        // Show success state briefly
        atcBtn.querySelector('.akk-product-shop__atc-text').textContent = 'Added!';

        // Try to open cart drawer if it exists
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && cartDrawer.open) {
          cartDrawer.open();
        }

        setTimeout(() => {
          atcBtn.classList.remove('is-loading');
          atcBtn.disabled = false;
          atcBtn.querySelector('.akk-product-shop__atc-text').textContent = 'Add to cart';
        }, 1200);

      } catch (err) {
        atcBtn.classList.remove('is-loading');
        atcBtn.disabled = false;
        if (errorEl) {
          errorEl.textContent = err.message;
          errorEl.classList.add('is-visible');
        }
      }
    });
  }

  async function updateCartCount() {
    try {
      const resp = await fetch('/cart.js');
      const cart = await resp.json();
      const countBubble = document.querySelector('.cart-count-bubble span[aria-hidden]');
      if (countBubble) {
        countBubble.textContent = cart.item_count > 99 ? '99+' : cart.item_count;
      }
      // Also update cart empty/full icon
      const cartIconWrap = document.getElementById('cart-icon-bubble');
      if (cartIconWrap) {
        const svgWrapper = cartIconWrap.querySelector('.svg-wrapper');
        if (svgWrapper) {
          if (cart.item_count > 0) {
            // Ensure count bubble is visible
            const bubble = cartIconWrap.querySelector('.cart-count-bubble');
            if (bubble) bubble.style.display = 'block';
          }
        }
      }
    } catch (e) {
      // Silently fail
    }
  }

})();
